import { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newUuid } from '@shared';
import {
  postOpenAiCompletionRequest,
  fetchOpenAiCompletionByRequestIdRequest,
  resetOpenAiCompletionState,
} from '@client/model/openAiCompletionModel';
import { selectOpenAiCompletion } from '@client/model/selectors';
import { usePolling } from './usePolling';

// OpenAI Completion Status Constants
// eslint-disable-next-line @typescript-eslint/naming-convention
export const COMPLETION_STATUS = {
  IDLE: 'idle',
  CREATING: 'creating',
  POLLING: 'polling',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export type CompletionStatus = (typeof COMPLETION_STATUS)[keyof typeof COMPLETION_STATUS];

interface UseOpenAiCompletionOptions {
  onSuccess?: (result: OpenAiCompletionDbRecord) => void;
  onError?: (error: string) => void;
  pollingInterval?: number;
  maxPollingAttempts?: number;
}

export function useOpenAiCompletion(options: UseOpenAiCompletionOptions = {}) {
  const { onSuccess, onError, pollingInterval = 2000, maxPollingAttempts = 30 } = options;

  const dispatch = useDispatch();
  const openAiState = useSelector(selectOpenAiCompletion);

  const [requestId, setRequestId] = useState<string | null>(null);
  const [status, setStatus] = useState<CompletionStatus>(COMPLETION_STATUS.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<string | null>(null);
  const [result, setResult] = useState<OpenAiCompletionDbRecord | null>(null);

  const pollingAttemptsRef = useRef(0);
  const callbacksRef = useRef({ onSuccess, onError });

  useEffect(() => {
    callbacksRef.current = { onSuccess, onError };
  }, [onSuccess, onError]);

  const currentCompletion = requestId ? openAiState.completions[requestId] || null : null;
  const currentError = requestId ? openAiState.errors?.[requestId] || null : null;

  const isFinished = (c: OpenAiCompletionDbRecord | null) =>
    c &&
    (c.status === COMPLETION_STATUS.COMPLETED ||
      c.status === COMPLETION_STATUS.ERROR ||
      !!c.results?.trim());

  useEffect(() => {
    if (!currentCompletion || !isFinished(currentCompletion)) return;

    if (currentCompletion.status === COMPLETION_STATUS.ERROR || currentCompletion.error) {
      const msg = currentCompletion.error ?? 'Completion failed';
      setStatus(COMPLETION_STATUS.ERROR);
      setError(msg);
      setResult(null);
      callbacksRef.current.onError?.(msg);
    } else {
      setStatus(COMPLETION_STATUS.COMPLETED);
      setError(null);
      setResult(currentCompletion);
      callbacksRef.current.onSuccess?.(currentCompletion);
    }
  }, [currentCompletion]);

  // Handle both creation and polling errors
  useEffect(() => {
    if (!currentError) return;

    const statusCode = currentError.statusCode ?? null;
    const isRateLimitError =
      statusCode === '429' || (typeof statusCode === 'number' && statusCode === 429);

    // Provide a more descriptive error message for rate limit errors
    const msg = isRateLimitError
      ? 'You have reached the request limit for AI analysis. Please try again in a few minutes.'
      : currentError.message ?? 'Request failed';

    setStatus(COMPLETION_STATUS.ERROR);
    setError(msg);
    setErrorStatusCode(statusCode);
    // Don't clear result for rate limit errors - preserve previous results if available
    if (!isRateLimitError) {
      setResult(null);
    }
    callbacksRef.current.onError?.(msg);
  }, [currentError]);

  const createCompletion = useCallback(
    (prompt: string, options: Partial<OpenAiCompletionRequest>) => {
      const id = newUuid();
      setRequestId(id);
      setStatus(COMPLETION_STATUS.CREATING);
      setError(null);
      setResult(null);
      pollingAttemptsRef.current = 0;

      dispatch(postOpenAiCompletionRequest({ prompt, requestId: id, ...options }));
      return id;
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    setRequestId(null);
    setStatus(COMPLETION_STATUS.IDLE);
    setError(null);
    setResult(null);
    pollingAttemptsRef.current = 0;
    dispatch(resetOpenAiCompletionState());
  }, []);

  const shouldPoll =
    requestId &&
    status !== COMPLETION_STATUS.COMPLETED &&
    status !== COMPLETION_STATUS.ERROR &&
    !isFinished(currentCompletion) &&
    !currentError &&
    pollingAttemptsRef.current < maxPollingAttempts;

  const { isPolling } = usePolling({
    shouldPoll: !!shouldPoll,
    pollingFunction: useCallback(() => {
      if (!requestId) return;

      pollingAttemptsRef.current += 1;

      if (pollingAttemptsRef.current >= maxPollingAttempts) {
        const msg = 'Polling timeout: Maximum attempts reached';
        setStatus(COMPLETION_STATUS.ERROR);
        setError(msg);
        callbacksRef.current.onError?.(msg);
        return;
      }

      setStatus(COMPLETION_STATUS.POLLING);
      dispatch(fetchOpenAiCompletionByRequestIdRequest({ requestId }));
    }, [dispatch, requestId, maxPollingAttempts]),
    interval: pollingInterval,
    dependencies: [requestId, status, currentCompletion, currentError],
  });

  return {
    createCompletion,
    status,
    result,
    error,
    errorStatusCode,
    isPolling,
    reset,
  };
}
